import { useEffect, useRef, useState } from "react";
import { FileButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";

/** Same per-file cap as the Dropzone. */
const MAX_PHOTO_SIZE = 10 * 1024 ** 2;

interface PhotoInputProps {
  value: File[];
  onChange: (files: File[]) => void;
  /** Most photos allowed; the add tile disappears when reached. */
  max?: number;
  /** Accepted image types. JPEG and PNG by default; phones convert other formats (HEIC) on pick. */
  accept?: string;
  /** Shown next to the tiles, which also get a red border. */
  error?: string;
  "data-testid"?: string;
}

/**
 * A row of photo thumbnails with an add tile. The picker has no `capture`, so phones and tablets offer both
 * the camera and the photo library.
 */
export default function PhotoInput({
  value,
  onChange,
  max = Infinity,
  accept = "image/jpeg,image/png",
  error,
  "data-testid": testId,
}: PhotoInputProps) {
  const { t } = useTranslation();
  const resetRef = useRef<() => void>(null);
  const [rejected, setRejected] = useState<"wrongType" | "tooLarge" | null>(null);
  const previewUrls = usePreviewUrls(value);
  const acceptedTypes = accept.split(",").map((type) => type.trim());

  const addPhotos = (files: File[]) => {
    // Lets the same file be picked again after it was removed.
    resetRef.current?.();
    // `accept` only steers the picker ("All files" gets past it), so the type is checked here too.
    const isWrongType = (file: File) => !acceptedTypes.includes(file.type);
    const isTooLarge = (file: File) => file.size > MAX_PHOTO_SIZE;
    const accepted = files.filter((file) => !isWrongType(file) && !isTooLarge(file)).slice(0, max - value.length);
    setRejected(files.some(isWrongType) ? "wrongType" : files.some(isTooLarge) ? "tooLarge" : null);
    if (accepted.length) onChange([...value, ...accepted]);
  };

  const removePhoto = (index: number) => onChange(value.filter((_, i) => i !== index));

  const message =
    rejected === "wrongType"
      ? t("common:photos.wrongType")
      : rejected === "tooLarge"
        ? t("common:photos.tooLarge")
        : error;

  return (
    <div className={styles.root} data-testid={testId}>
      {value.map((file, index) => (
        <div
          key={`${index}-${file.name}-${file.lastModified}`}
          className={styles.thumb}
          style={previewUrls[index] ? { backgroundImage: `url(${previewUrls[index]})` } : undefined}
          role="img"
          aria-label={file.name}
        >
          <button
            type="button"
            className={styles.remove}
            onClick={() => removePhoto(index)}
            aria-label={t("common:photos.remove")}
          >
            <SVGAsset name="close-white" width={8} height={8} alt="" />
          </button>
        </div>
      ))}
      {value.length < max && (
        <FileButton onChange={addPhotos} accept={accept} multiple resetRef={resetRef}>
          {(props) => (
            <button {...props} type="button" className={`${styles.add} ${error ? styles.addError : ""}`}>
              <SVGAsset name="plus-circle" width={14} height={14} alt="" />
              <Text span display="technical" size="xxs" tt="uppercase">
                {t("common:photos.add")}
              </Text>
            </button>
          )}
        </FileButton>
      )}
      {message && (
        <Text size="xs" color="error">
          {message}
        </Text>
      )}
    </div>
  );
}

/** Object URLs for the thumbnails, revoked when the files change or the input unmounts. */
const usePreviewUrls = (files: File[]) => {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const next = files.map((file) => URL.createObjectURL(file));
    setUrls(next);
    return () => next.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  return urls;
};
